import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Textarea,
} from '@chakra-ui/react';
import { Editor } from '@tiptap/core';

import { useEffect, useState } from 'react';

import { TTEditorModeEnum, dukeTTEditorState } from '../ReactFlow/dukeTTEditorState';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { QuizQuestionEditable, QuizQuestionPreview } from './QuizQuestionEditable';
import { useSnapshot } from 'valtio';
import {
  IFreeQuestionOption,
  IQuestionOption,
  QuestionOptions,
} from '@duke-hq/svc-api/provider/trpc/routers/tenant/IQuestionOption';
import { questionIdOf } from '@duke-hq/svc-api/student/trpc/routers/resourceEnrolments/helpers/flowContentSchemas';
import { createEditorContentId } from './createEditorContentId';
import { asHint, asQuestionOptions } from './questionJsonGuards';
import { editorSettingsState } from '../../state/editorSettingsState';
import { NodeData } from '../ReactFlow/nodes/flowEditorBlockNodeTypes';
import { HintType, QuizHint } from '../ReactFlow/nodes/blocknode/QuizHint';

export const dukeQuestionBlockType = 'reactComponent'; // TODO rename to dukeQuestion

/**
 * this is used in page editor and flow editor
 */
export function DukeQuestionOptionsEditor(props: {
  questionId: string;
  editor: Editor;
  nodeData?: NodeData;
}) {
  const createQuestion = trpcAdmin.questions.create.useMutation();

  const questionQuery = trpcAdmin.questions.byId.useQuery(props.questionId, {
    enabled: !!props.questionId,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });
  const { editor } = props;
  const { mode } = useSnapshot(dukeTTEditorState);

  const updateQuestion = trpcAdmin.questions.update.useMutation({
    onSuccess: () => {
      questionQuery.refetch();
    },
  });
  const updateFreeQuestion = trpcAdmin.questions.updateFreeQuestion.useMutation();

  const [options, setOptions] = useState<QuestionOptions>([]);
  const [hint, setHint] = useState<HintType>(undefined);

  useEffect(() => {
    if (questionQuery.data) {
      editorSettingsState.resource.question[questionQuery.data.id] = questionQuery.data;
    }
  }, [questionQuery.data]);

  useEffect(() => {
    const parsedOptions = asQuestionOptions(questionQuery.data?.options);
    if (parsedOptions) {
      setOptions(parsedOptions);
    }
  }, [questionQuery.data?.options]);

  useEffect(() => {
    const parsedHint = asHint(questionQuery.data?.hint);
    if (parsedHint) {
      setHint(parsedHint);
    }
  }, [questionQuery.data?.hint]);

  const question = questionQuery.data;

  if (questionQuery.isLoading || !options || !question) {
    return <Spinner />;
  }
  const isEdit = mode === TTEditorModeEnum.EDIT;

  const updateOptions = async (newOptions: Array<IQuestionOption>) => {
    if (mode !== TTEditorModeEnum.EDIT || !props.editor) {
      return;
    }

    if (question.first_published_in_resource_version_id) {
      // this question is already published so we need to create a new one
      const newQuestion = await createQuestion.mutateAsync({
        ...question,
        content: question.content,
        hint: asHint(question.hint),
        options: newOptions,
        kind: question.kind,
        resourceId: question.resource_id ?? undefined,
        id: createEditorContentId(),
      });
      const content = editor.getJSON().content;

      if (content) {
        editor?.commands.setContent(
          content.map((node) => {
            if (questionIdOf(node.content?.[0]) === props.questionId) {
              return {
                type: dukeQuestionBlockType,
                content: newQuestion.content,
                attrs: {
                  questionId: newQuestion.id,
                },
              };
            }
            return node;
          }),
        );
      }
    } else {
      // this question is not published yet, so we can just update it
      setOptions(newOptions); // optimistic update here

      await updateQuestion.mutateAsync({
        id: props.questionId,
        options: newOptions,
      });
    }
  };

  const updateHint = async (hint: any) => {
    if (question.first_published_in_resource_version_id) {
      const newQuestion = await createQuestion.mutateAsync({
        ...question,
        content: question.content,
        hint: hint || null,
        kind: question.kind,
        resourceId: question.resource_id ?? undefined,
        id: createEditorContentId(),
      });

      const content = editor.getJSON().content;

      if (content) {
        editor?.commands.setContent(
          content.map((node) => {
            if (questionIdOf(node.content?.[0]) === props.questionId) {
              return {
                type: dukeQuestionBlockType,
                content: newQuestion.content,
                attrs: {
                  questionId: newQuestion.id,
                },
              };
            }
            return node;
          }),
        );
      }
    } else {
      setHint(hint); // optimistic update here

      await updateQuestion.mutateAsync({
        id: props.questionId,
        hint: hint || null,
      });
    }
  };

  if (Array.isArray(options)) {
    if (isEdit) {
      return (
        <>
          <QuizQuestionEditable options={options} update={updateOptions} question={question} />
          <HStack>
            <QuizHint hint={hint} updateHint={updateHint} />
          </HStack>
        </>
      );
    }
    return (
      <>
        <QuizQuestionPreview options={options} question={question}></QuizQuestionPreview>
        <QuizHint hint={hint} updateHint={updateHint} />
      </>
    );
  } else {
    // this is a TEXT question
    const freeQuestionOptions = options as {
      minLength: number;
      maxLength: number;
    };
    const updateFreeQuestionOptions = async (newOptions: IFreeQuestionOption) => {
      setOptions(newOptions); // optimistic update here

      await updateFreeQuestion.mutateAsync({
        id: props.questionId,
        options: newOptions,
      });
    };

    return (
      <Flex w={'full'} direction={'column'}>
        {isEdit ? (
          <Flex mb={'12px'} w={320}>
            <FormControl>
              <FormLabel flexDirection={'column'}>Minimum length:</FormLabel>
              <Input
                defaultValue={freeQuestionOptions.minLength}
                w={136}
                onChange={(event) => {
                  // TODO debounce
                  updateFreeQuestionOptions({
                    ...freeQuestionOptions,
                    minLength: parseInt(event.target.value) ?? 0,
                  });
                }}
              ></Input>
            </FormControl>
            <FormControl>
              <FormLabel flexDirection={'column'}>Maximum length:</FormLabel>
              <Input
                defaultValue={freeQuestionOptions.maxLength}
                w={136}
                onChange={(event) => {
                  // TODO debounce
                  updateFreeQuestionOptions({
                    ...freeQuestionOptions,
                    maxLength: parseInt(event.target.value) ?? 0,
                  });
                }}
              ></Input>
            </FormControl>
          </Flex>
        ) : null}
        <Box
          p={1}
          m={0}
          borderRadius="lg"
          border={'2px solid'}
          borderColor="grey"
          w={'full'}
          role="group"
        >
          <Textarea disabled={mode === TTEditorModeEnum.EDIT} resize={'none'} />{' '}
        </Box>
      </Flex>
    );
  }
}
